import Store from 'repatch';

import State from '../core/State';
import { UserOptimizer } from '../graphql/generated/graphql';
import ApiErrorHandler from '../service/ApiErrorHandler';
import ErrorPresenter from '../service/ErrorPresenter';
import IntercomService from '../service/IntercomService';
import AuthenticationWebApi from '../service/persistent/AuthenticationWebApi';
import SessionStorage from '../service/persistent/SessionStorage';
import SessionWebApi from '../service/persistent/SessionWebApi';
import UserWebApi, { OptimizerUserNotFound } from '../service/persistent/UserWebApi';
import { disposeUser, finishAuthentication, setSignedInUser, startAuthentication } from '../thunk/authentication';

class AuthenticationUsecase {
  private store: Store<State>;
  private errorPresenter: ErrorPresenter;
  private apiErrorHandler: ApiErrorHandler;
  private authenticationWebApi: AuthenticationWebApi;
  private intercomService: IntercomService;
  private userWebApi: UserWebApi;

  constructor({
    store,
    errorPresenter,
    apiErrorHandler,
    authenticationWebApi,
    intercomService,
    userWebApi,
  }: {
    store: Store<State>;
    errorPresenter: ErrorPresenter;
    apiErrorHandler: ApiErrorHandler;
    authenticationWebApi: AuthenticationWebApi;
    intercomService: IntercomService;
    sessionStorage: SessionStorage;
    sessionWebApi: SessionWebApi;
    userWebApi: UserWebApi;
  }) {
    this.store = store;
    this.errorPresenter = errorPresenter;
    this.apiErrorHandler = apiErrorHandler;
    this.authenticationWebApi = authenticationWebApi;
    this.intercomService = intercomService;
    this.userWebApi = userWebApi;
  }

  public needSignedIn = (): void => {
    const user = this.store.getState().authentication.signedInUser;

    if (!user) {
      const err = new Error('message.page_that_require_signin');
      this.errorPresenter.showAsNonFatal(err);

      throw err;
    }
  };

  /**
   * logoutAPIを叩いてlogoutする
   */
  public signOut = async (): Promise<void> => {
    const user = this.store.getState().authentication.signedInUser;
    if (!user) {
      throw new Error('It should not be possible to reach here.');
    }

    await this.disposeSession();
    this.disposeUser();
    this.anonymizeExternalServices();
  };

  public signInWithPreviousSession = async (): Promise<void> => {
    this.store.dispatch(startAuthentication());

    try {
      await this.prepareUser();
      const user = this.store.getState().authentication.signedInUser;
      if (!user) {
        return;
      }
    } catch (err) {
      this.authenticationFailed(err);
    } finally {
      this.store.dispatch(finishAuthentication());
    }
  };

  private prepareUser = async (): Promise<void> => {
    let user: UserOptimizer | null;

    try {
      user = await this.userWebApi.getMe();
    } catch (err) {
      if (err instanceof OptimizerUserNotFound) {
        user = null;
      } else {
        throw err;
      }
    }

    if (user === null) {
      user = await this.userWebApi.signUpAsOptimizer();
    }

    this.store.dispatch(setSignedInUser(user));
    window.newrelic.setCustomAttribute('user_id', user.id);
    window.newrelic.setCustomAttribute('user_email', user.email || '');

    this.identifyExternalServices(user);
  };

  /**
   * sessionを破棄する
   */
  private disposeSession = async (): Promise<void> => {
    try {
      await this.authenticationWebApi.signOut();
      return Promise.resolve();
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject();
    }
  };

  private disposeUser = () => {
    this.store.dispatch(disposeUser());
    try {
      window.newrelic.setCustomAttribute('user_id', 'signed_out');
      window.newrelic.setCustomAttribute('user_email', 'signed_out');
    } catch (_) {
      console.log('Ignore NewRelic');
    }
  };

  private authenticationFailed = (err: Error, message = 'message.authentication_has_failed'): void => {
    if (err instanceof OptimizerUserNotFound) {
      message = 'message.not_registered';
    }

    this.errorPresenter.showAsNonFatal(new Error(message));
  };

  private identifyExternalServices = (user: UserOptimizer): void => {
    this.intercomService.identify(user);
  };

  private anonymizeExternalServices = (): void => {
    this.intercomService.anonymize();
  };
}

export default AuthenticationUsecase;
